import { __awaiter } from '../node_modules/tslib/tslib.es6.js';
import { jsx, jsxs, Fragment } from 'react/jsx-runtime';
import { useBrandAppConfig } from '../api/cache/appConfigQueryOptions.js';
import { useState } from 'react';
import { Toaster, toast } from 'sonner';
import { ConciergeChatAreaV2 } from './components/ConciergeChatAreaV2.js';
import { ConciergeHeader } from './components/ConciergeHeader/index.js';
import { ConciergeSidebar } from './components/ConciergeSidebar.js';
import { useAutoScroll } from './hooks/useAutoScroll.js';
import { useAutoSendFromUrl } from './hooks/useAutoSendFromUrl.js';
import { useConversationLoader } from './hooks/useConversationLoader.js';
import { useConciergeContext } from './ConciergeContextProvider.js';
import { useStreamingSearch } from './hooks/useStreamingSearch.js';
import { useUrlSync } from './hooks/useUrlSync.js';
import { Drawer } from '@mui/material';

const ConciergeContainer = ({ layout = 'header', isEmbedded, cartCount = 0, onBagClick, isCheckoutDrawerOpen = false, CheckoutDrawerContent, onVariantSelect, userProfile, onSignOutClick, onHomeClick, userFirstName, }) => {
    // Default isEmbedded based on layout: true for sidebar (landing-page), false for header (mobile)
    const effectiveIsEmbedded = isEmbedded !== null && isEmbedded !== void 0 ? isEmbedded : layout === 'sidebar';
    // Conversation state (centralized via context - includes apiBaseUrl)
    const conversationState = useConciergeContext();
    // Brand config
    const { data: config } = useBrandAppConfig(conversationState.apiBaseUrl);
    const params = new URLSearchParams(window.location.search);
    const brandId = params.get('brandId') || null;
    const appConfigId = params.get('appConfigId') || null;
    // Input state
    const [searchInputValue, setSearchInputValue] = useState('');
    // Auto-scroll behavior
    const { chatWrapperRef: _chatWrapperRef, messagesEndRef: _messagesEndRef, shouldScrollOnNextMessageRef, scrollToBottom, } = useAutoScroll(conversationState.conversationHistory.length, conversationState.isActivelyStreaming, conversationState.streamingAIResponse, conversationState.conversationHistory.length === 0);
    // Handle stream errors - restore input and show appropriate toast
    const handleStreamError = (errorType, originalMessage) => {
        setSearchInputValue(originalMessage);
        const errorMessages = {
            rate_limit: 'Too many requests. Please wait a moment before trying again.',
            network: 'Network error. Please check your connection and try again.',
            server: 'Something went wrong on our end. Please try again.',
            unknown: 'Something went wrong. Please try again.',
        };
        toast.error(errorMessages[errorType]);
    };
    // Streaming search
    const { performSearch, cancelStream } = useStreamingSearch({
        brandId,
        appConfigId,
        conversationState,
        scrollToBottom,
        shouldScrollOnNextMessageRef,
        onStreamError: handleStreamError,
    });
    // Handle search submission
    const handleSearch = (searchTerm, imageData, imageMimeType) => __awaiter(void 0, void 0, void 0, function* () {
        setSearchInputValue('');
        yield performSearch(searchTerm, imageData, imageMimeType);
    });
    // Handle update feedback
    const handleUpdateFeedback = (messageIndex, feedback) => {
        // Update the feedback in the conversation history
        conversationState.setConversationHistory((prev) => {
            return prev.map((msg, idx) => {
                if (idx === messageIndex) {
                    return Object.assign(Object.assign({}, msg), { feedback: feedback || undefined });
                }
                return msg;
            });
        });
    };
    // Handle conversation selection from history
    const handleConversationSelect = (conversationId) => {
        // If we're already viewing this conversation, just reload it by updating the ID
        // This ensures a fresh load without the jarring clear + reload
        if (conversationState.conversationId === conversationId) {
            // Force reload by temporarily clearing and resetting the ID
            // This will trigger useConversationLoader to reload
            conversationState.setConversationId(null);
            // Use setTimeout to ensure the state update is processed
            setTimeout(() => {
                conversationState.setConversationId(conversationId);
            }, 0);
            return;
        }
        // Clear current conversation state before loading new one
        conversationState.setConversationHistory([]);
        conversationState.setProductMetadataMap(new Map());
        conversationState.setStreamingProductGroups(new Map());
        conversationState.setPreviewSearchQueries([]);
        conversationState.setPreviewQueryProducts([]);
        conversationState.setIsWaitingForFollowUpQueries(false);
        conversationState.setStreamingAIResponse('');
        conversationState.setCurrentStatus('');
        conversationState.setFollowUpQuestions([]);
        // Update URL with the selected conversation ID
        const params = new URLSearchParams(window.location.search);
        params.set('conversationId', conversationId);
        const newUrl = `${window.location.pathname}?${params.toString()}`;
        window.history.pushState({}, '', newUrl);
        // Update conversation state
        conversationState.setConversationId(conversationId);
    };
    // Handle compose button click (go back to welcome screen)
    const handleComposeClick = () => {
        // Clear conversation state
        conversationState.setConversationHistory([]);
        conversationState.setProductMetadataMap(new Map());
        conversationState.setStreamingProductGroups(new Map());
        conversationState.setPreviewSearchQueries([]);
        conversationState.setPreviewQueryProducts([]);
        conversationState.setIsWaitingForFollowUpQueries(false);
        conversationState.setStreamingAIResponse('');
        conversationState.setCurrentStatus('');
        conversationState.setFollowUpQuestions([]);
        conversationState.setConversationId(null);
        // Remove conversationId from URL
        const params = new URLSearchParams(window.location.search);
        params.delete('conversationId');
        const newUrl = params.toString() ? `${window.location.pathname}?${params.toString()}` : window.location.pathname;
        window.history.pushState({}, '', newUrl);
    };
    // Conversation loader
    const { isLoadingConversationQuery } = useConversationLoader({
        conversationId: conversationState.conversationId,
        isActivelyStreaming: conversationState.isActivelyStreaming,
        conversationHistoryLength: conversationState.conversationHistory.length,
        apiBaseUrl: conversationState.apiBaseUrl,
        onConversationLoaded: (messages, productMap, surveyMap, followUpQuestions, previewSearchQueries, previewQueryProducts) => {
            conversationState.setProductMetadataMap(productMap);
            conversationState.setSurveyMetadataMap(surveyMap);
            conversationState.setConversationHistory(messages);
            conversationState.setFollowUpQuestions(followUpQuestions);
            conversationState.setPreviewSearchQueries(previewSearchQueries);
            conversationState.setPreviewQueryProducts(previewQueryProducts);
        },
        onConversationNotFound: () => {
            const params = new URLSearchParams(window.location.search);
            params.delete('conversationId');
            const newUrl = params.toString() ? `${window.location.pathname}?${params.toString()}` : window.location.pathname;
            window.history.replaceState({}, '', newUrl);
            conversationState.setConversationId(null);
        },
    });
    // Sync conversationId with URL
    useUrlSync(conversationState.conversationId);
    // Auto-send from URL params
    useAutoSendFromUrl({
        isActivelyStreaming: conversationState.isActivelyStreaming,
        conversationLength: conversationState.conversationHistory.length,
        onSearch: handleSearch,
    });
    const showWelcome = conversationState.conversationHistory.length === 0 && !isLoadingConversationQuery;
    // Shared chat area props
    const chatAreaProps = {
        appName: config === null || config === void 0 ? void 0 : config.appName,
        brandId,
        conversationId: conversationState.conversationId,
        apiBaseUrl: conversationState.apiBaseUrl,
        isEmbedded: effectiveIsEmbedded,
        userFirstName,
        conversationHistory: conversationState.conversationHistory,
        productMetadataMap: conversationState.productMetadataMap,
        surveyMetadataMap: conversationState.surveyMetadataMap,
        streamingProductGroups: conversationState.streamingProductGroups,
        previewSearchQueries: conversationState.previewSearchQueries,
        previewQueryProducts: conversationState.previewQueryProducts,
        followUpQuestions: conversationState.followUpQuestions,
        streamingFeaturedProduct: conversationState.streamingFeaturedProduct,
        streamingStoreLocator: conversationState.streamingStoreLocator,
        isActivelyStreaming: conversationState.isActivelyStreaming,
        streamingAIResponse: conversationState.streamingAIResponse,
        currentStatus: conversationState.currentStatus,
        isWaitingForFollowUpQueries: conversationState.isWaitingForFollowUpQueries,
        isLoadingConversationQuery,
        handleSearch,
        onUpdateFeedback: handleUpdateFeedback,
        onVariantSelect,
        onStop: cancelStream,
        searchInputValue,
        setSearchInputValue,
    };
    // Checkout drawer (shared between layouts)
    const checkoutDrawer = CheckoutDrawerContent && (jsx(Drawer, Object.assign({ id: "cart-popover", open: isCheckoutDrawerOpen, sx: {
            zIndex: 1300,
            width: 462,
            maxWidth: '100%',
        }, PaperProps: {
            sx: {
                boxShadow: 'none',
                borderRadius: '0',
                maxWidth: '100%',
            },
        }, anchor: "right", disableAutoFocus: true, disableEnforceFocus: true, disableRestoreFocus: true }, { children: jsx(CheckoutDrawerContent, {}) })));
    // Sidebar layout - row direction with sidebar on left
    if (layout === 'sidebar') {
        return (jsxs(Fragment, { children: [jsx(Toaster, {}), jsxs("div", Object.assign({ className: "concierge-root", style: {
                        display: 'flex',
                        flexDirection: 'row',
                        height: '100dvh',
                        minHeight: '-webkit-fill-available',
                    } }, { children: [jsx(ConciergeSidebar, { onConversationSelect: handleConversationSelect, onComposeClick: handleComposeClick, currentConversationId: conversationState.conversationId, userProfile: userProfile, apiBaseUrl: conversationState.apiBaseUrl, onSignOutClick: onSignOutClick, onHomeClick: onHomeClick }), jsx("div", Object.assign({ className: "tw:relative tw:flex tw:flex-1 tw:flex-col", style: { minWidth: 0 } }, { children: jsx(ConciergeChatAreaV2, Object.assign({}, chatAreaProps)) }))] })), checkoutDrawer] }));
    }
    // Header layout (default) - column direction with header on top
    return (jsxs(Fragment, { children: [jsx(Toaster, {}), jsxs("div", Object.assign({ className: "concierge-root", style: {
                    display: 'flex',
                    flexDirection: 'column',
                    height: '100dvh',
                    minHeight: '-webkit-fill-available',
                } }, { children: [jsx(ConciergeHeader, { onConversationSelect: handleConversationSelect, onComposeClick: handleComposeClick, showComposeIcon: !showWelcome, cartCount: cartCount, onBagClick: onBagClick }), jsx(ConciergeChatAreaV2, Object.assign({}, chatAreaProps))] })), checkoutDrawer] }));
};

export { ConciergeContainer as default };
//# sourceMappingURL=ConciergeContainer.js.map
